import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Save, 
  X, 
  BookOpen, 
  Clock, 
  Award,
  AlertCircle,
  CheckCircle,
  ArrowLeft,
  Users,
  UserCheck,
  Building
} from 'lucide-react';
import { useModules } from '../contexts/ModuleContext';
import { useUsers } from '../contexts/UserContext';
import AssignedUsersModal from './AssignedUsersModal';
import { graphqlService } from '../services/graphqlService';

const ModuleManagement = ({ onBack }) => {
  const { modules, createModule, updateModule, deleteModule } = useModules();
  const { users, getAllRoles, getAllDepartments, getAllDesignations, getAllSpecializations } = useUsers();
  const [isEditing, setIsEditing] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    title: '',
    description: '',
    duration: 30,
    difficulty: 'Beginner',
    tags: [],
    content: {
      sections: []
    },
    quiz: [],
    assignments: {
      roles: [],
      users: [],
      departments: [],
      designations: [],
      specializations: []
    }
  });
  const [currentSection, setCurrentSection] = useState(0);
  const [currentQuiz, setCurrentQuiz] = useState(0);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showAssignedUsers, setShowAssignedUsers] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);

  const difficultyLevels = ['Beginner', 'Intermediate', 'Advanced'];
  const [availableTags, setAvailableTags] = useState([]);
  const [newTagName, setNewTagName] = useState('');
  const [isCreatingTag, setIsCreatingTag] = useState(false);
  const icons = [
    { name: 'FileText', component: 'FileText' },
    { name: 'Shield', component: 'Shield' },
    { name: 'Globe', component: 'Globe' },
    { name: 'Award', component: 'Award' },
    { name: 'Users', component: 'Users' },
    { name: 'BookOpen', component: 'BookOpen' },
    { name: 'CheckCircle', component: 'CheckCircle' },
    { name: 'Star', component: 'Star' },
    { name: 'Briefcase', component: 'Briefcase' },
    { name: 'GraduationCap', component: 'GraduationCap' },
    { name: 'Heart', component: 'Heart' },
    { name: 'Activity', component: 'Activity' },
    { name: 'Syringe', component: 'Syringe' },
    { name: 'User', component: 'User' },
    { name: 'Brain', component: 'Brain' },
    { name: 'Stethoscope', component: 'Stethoscope' },
    { name: 'Pill', component: 'Pill' }
  ];

  useEffect(() => {
    const loadTags = async () => {
      try {
        const activeTags = await graphqlService.getActiveTags();
        setAvailableTags(activeTags || []);
      } catch (error) {
        console.error('Error loading tags:', error);
        setAvailableTags([]);
      }
    };

    loadTags();
  }, []);

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    
    const tagName = newTagName.trim();
    const tagNameUpper = tagName.toUpperCase();
    
    // Check if tag already exists
    const existingTag = availableTags.find(t => t.name === tagNameUpper);
    if (existingTag) {
      alert(`Tag "${tagNameUpper}" already exists.`);
      setNewTagName('');
      return;
    }
    
    setIsCreatingTag(true);
    try {
      // Create the tag with required fields
      const newTag = await graphqlService.createTag({
        name: tagNameUpper,
        displayName: tagName, // Use original case for display
        category: 'general',
        isActive: true,
        order: 0
      });
      
      // Add to available tags
      setAvailableTags([...availableTags, newTag]);
      
      // Automatically select the new tag
      const currentTags = formData.tags || [];
      if (!currentTags.includes(newTag.name)) {
        setFormData({
          ...formData,
          tags: [...currentTags, newTag.name]
        });
      }
      
      // Clear input
      setNewTagName('');
    } catch (error) {
      console.error('Error creating tag:', error);
      alert(`Failed to create tag: ${error.message || 'Unknown error'}`);
    } finally {
      setIsCreatingTag(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (formData.duration <= 0) newErrors.duration = 'Duration must be greater than 0';
    if (formData.content.sections.length === 0) newErrors.sections = 'At least one section is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreate = () => {
    setFormData({
      id: '',
      title: '',
      description: '',
      duration: 30,
      difficulty: 'Beginner',
      tags: [],
      content: {
        sections: []
      },
      quiz: [],
      assignments: {
        roles: [],
        users: [],
        departments: []
      }
    });
    setCurrentSection(0);
    setCurrentQuiz(0);
    setErrors({});
    setSelectedUsers([]);
    setNewTagName('');
    setShowCreateForm(true);
    setIsEditing(false);
    setEditingModule(null);
  };

  const handleEdit = (module) => {
    setFormData({
      ...module,
      duration: typeof module.duration === 'number' ? module.duration : parseInt(module.duration, 10) || 30,
      tags: module.tags || [],
      content: {
        sections: module.content.sections || []
      },
      quiz: module.quiz || [],
      assignments: module.assignments || {
        roles: [],
        users: [],
        departments: [],
        designations: [],
        specializations: []
      }
    });
    setNewTagName('');
    setCurrentSection(0);
    setCurrentQuiz(0);
    setErrors({});
    setSelectedUsers(module.assignments?.users || []);
    setShowCreateForm(true);
    setIsEditing(true);
    setEditingModule(module);
  };

  const handleSave = () => {
    if (!validateForm()) return;

    const moduleData = {
      ...formData,
      icon: formData.icon || 'BookOpen'
    };

    if (isEditing) {
      updateModule(editingModule.id, moduleData);
    } else {
      createModule(moduleData);
    }

    setShowCreateForm(false);
    setIsEditing(false);
    setEditingModule(null);
  };

  const handleDelete = (moduleId) => {
    deleteModule(moduleId);
    setShowDeleteConfirm(null);
  };

  const handleAddSection = () => {
    const newSection = {
      title: '',
      content: '',
      video: null
    };
    setFormData({
      ...formData,
      content: {
        ...formData.content,
        sections: [...formData.content.sections, newSection]
      }
    });
    setCurrentSection(formData.content.sections.length);
  };

  const handleUpdateSection = (index, field, value) => {
    const updatedSections = [...formData.content.sections];
    updatedSections[index] = {
      ...updatedSections[index],
      [field]: value
    };
    setFormData({
      ...formData,
      content: {
        ...formData.content,
        sections: updatedSections
      }
    });
  };

  const handleRemoveSection = (index) => {
    const updatedSections = formData.content.sections.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      content: {
        ...formData.content,
        sections: updatedSections
      }
    });
    if (currentSection >= updatedSections.length) {
      setCurrentSection(Math.max(0, updatedSections.length - 1));
    }
  };

  const handleAddQuiz = () => {
    const newQuiz = {
      question: '',
      options: ['', '', '', ''],
      correct: 0
    };
    setFormData({
      ...formData,
      quiz: [...formData.quiz, newQuiz]
    });
    setCurrentQuiz(formData.quiz.length);
  };

  const handleUpdateQuiz = (index, field, value) => {
    const updatedQuiz = [...formData.quiz];
    updatedQuiz[index] = {
      ...updatedQuiz[index],
      [field]: value
    };
    setFormData({
      ...formData,
      quiz: updatedQuiz
    });
  };

  const handleRemoveQuiz = (index) => {
    const updatedQuiz = formData.quiz.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      quiz: updatedQuiz
    });
    if (currentQuiz >= updatedQuiz.length) {
      setCurrentQuiz(Math.max(0, updatedQuiz.length - 1));
    }
  };

  const getIconComponent = (iconName) => {
    // This would need to be implemented with actual icon components
    // For now, we'll use a simple text representation
    return `<${iconName} size={32} color="#1e40af" />`;
  };

  const handleRoleAssignment = (role, checked) => {
    const updatedRoles = checked
      ? [...formData.assignments.roles, role]
      : formData.assignments.roles.filter(r => r !== role);
    
    setFormData({
      ...formData,
      assignments: {
        ...formData.assignments,
        roles: updatedRoles
      }
    });
  };

  const handleDepartmentAssignment = (department, checked) => {
    const updatedDepartments = checked
      ? [...formData.assignments.departments, department]
      : formData.assignments.departments.filter(d => d !== department);
    
    setFormData({
      ...formData,
      assignments: {
        ...formData.assignments,
        departments: updatedDepartments
      }
    });
  };

  const handleUserAssignment = (userEmail, checked) => {
    const updatedUsers = checked
      ? [...formData.assignments.users, userEmail]
      : formData.assignments.users.filter(u => u !== userEmail);
    
    setFormData({
      ...formData,
      assignments: {
        ...formData.assignments,
        users: updatedUsers
      }
    });
  };

  const handleDesignationAssignment = (designation, checked) => {
    const updatedDesignations = checked
      ? [...formData.assignments.designations, designation]
      : formData.assignments.designations.filter(d => d !== designation);
    
    setFormData({
      ...formData,
      assignments: {
        ...formData.assignments,
        designations: updatedDesignations
      }
    });
  };

  const handleSpecializationAssignment = (specialization, checked) => {
    const updatedSpecializations = checked
      ? [...formData.assignments.specializations, specialization]
      : formData.assignments.specializations.filter(s => s !== specialization);
    
    setFormData({
      ...formData,
      assignments: {
        ...formData.assignments,
        specializations: updatedSpecializations
      }
    });
  };

  const filteredUsers = users.filter(user => 
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.department?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getAssignmentSummary = (module) => {
    const assignments = module.assignments || { 
      roles: [], 
      users: [], 
      departments: [], 
      designations: [], 
      specializations: [] 
    };
    const parts = [];
    
    if (assignments.roles && assignments.roles.length > 0) {
      parts.push(`${assignments.roles.length} role(s)`);
    }
    if (assignments.departments && assignments.departments.length > 0) {
      parts.push(`${assignments.departments.length} department(s)`);
    }
    if (assignments.designations && assignments.designations.length > 0) {
      parts.push(`${assignments.designations.length} designation(s)`);
    }
    if (assignments.specializations && assignments.specializations.length > 0) {
      parts.push(`${assignments.specializations.length} specialization(s)`);
    }
    if (assignments.users && assignments.users.length > 0) {
      parts.push(`${assignments.users.length} user(s)`);
    }
    
    return parts.length > 0 ? parts.join(', ') : 'Available to all';
  };

  const handleViewAssignedUsers = (module) => {
    setSelectedModule(module);
    setShowAssignedUsers(true);
  };

  if (showCreateForm) {
    return (
      <div className="container">
        <div className="module-form-header">
          <button onClick={() => setShowCreateForm(false)} className="btn btn-secondary">
            <ArrowLeft size={16} style={{ marginRight: '8px' }} />
            Back to Modules
          </button>
          <h1 className="text-2xl font-bold">
            {isEditing ? 'Edit Module' : 'Create New Module'}
          </h1>
        </div>

        <div className="module-form">
          <div className="form-section">
            <h2 className="text-xl font-bold mb-4">Basic Information</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Module Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className={errors.title ? 'error' : ''}
                  placeholder="Enter module title"
                />
                {errors.title && <span className="error-text">{errors.title}</span>}
              </div>

              <div className="form-group">
                <label>Duration (minutes) *</label>
                <input
                  type="number"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 0 })}
                  className={errors.duration ? 'error' : ''}
                  min="1"
                />
                {errors.duration && <span className="error-text">{errors.duration}</span>}
              </div>

              <div className="form-group">
                <label>Difficulty Level *</label>
                <select
                  value={formData.difficulty}
                  onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                >
                  {difficultyLevels.map(level => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Icon</label>
                <select
                  value={formData.icon || 'BookOpen'}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                >
                  {icons.map(icon => (
                    <option key={icon.name} value={icon.name}>{icon.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className={errors.description ? 'error' : ''}
                placeholder="Enter module description"
                rows="3"
              />
              {errors.description && <span className="error-text">{errors.description}</span>}
            </div>

            <div className="form-group">
              <label>Tags</label>
              <div className="tags-selector">
                <div className="tags-options">
                  {availableTags.map(tag => (
                    <div
                      key={tag._id || tag.name}
                      className={`tag-option ${formData.tags?.includes(tag.name) ? 'selected' : ''}`}
                      onClick={() => {
                        const currentTags = formData.tags || [];
                        if (currentTags.includes(tag.name)) {
                          setFormData({
                            ...formData,
                            tags: currentTags.filter(t => t !== tag.name)
                          });
                        } else {
                          setFormData({
                            ...formData,
                            tags: [...currentTags, tag.name]
                          });
                        }
                      }}
                    >
                      <span className={`tag-checkbox ${formData.tags?.includes(tag.name) ? 'checked' : ''}`}>
                        {formData.tags?.includes(tag.name) && '✓'}
                      </span>
                      {tag.displayName || tag.name}
                    </div>
                  ))}
                </div>
                
                {/* Add New Tag Section */}
                <div className="add-tag-section">
                  <div className="add-tag-input-group">
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Enter new tag name..."
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && newTagName.trim()) {
                          handleCreateTag();
                        }
                      }}
                      style={{ flex: 1, marginRight: '0.5rem' }}
                    />
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCreateTag}
                      disabled={!newTagName.trim() || isCreatingTag}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      {isCreatingTag ? 'Creating...' : <><Plus size={14} style={{ marginRight: '4px' }} />Add Tag</>}
                    </button>
                  </div>
                </div>
                
                {formData.tags && formData.tags.length > 0 && (
                  <div className="selected-tags-display">
                    <span className="selected-tags-label">Selected: </span>
                    <span className="selected-tags-list">{formData.tags.join(', ')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <h2 className="text-xl font-bold">Content Sections</h2>
              <button onClick={handleAddSection} className="btn btn-primary btn-sm">
                <Plus size={16} style={{ marginRight: '4px' }} />
                Add Section
              </button>
            </div>
            
            {errors.sections && <span className="error-text">{errors.sections}</span>}
            
            {formData.content.sections.length > 0 && (
              <div className="sections-tabs">
                {formData.content.sections.map((section, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSection(index)}
                    className={`tab ${currentSection === index ? 'active' : ''}`}
                  >
                    Section {index + 1}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveSection(index);
                      }}
                      className="remove-btn"
                    >
                      <X size={14} />
                    </button>
                  </button>
                ))}
              </div>
            )}

            {formData.content.sections.length > 0 && (
              <div className="section-editor">
                <div className="form-group">
                  <label>Section Title</label>
                  <input
                    type="text"
                    value={formData.content.sections[currentSection]?.title || ''}
                    onChange={(e) => handleUpdateSection(currentSection, 'title', e.target.value)}
                    placeholder="Enter section title"
                  />
                </div>

                <div className="form-group">
                  <label>Section Content</label>
                  <textarea
                    value={formData.content.sections[currentSection]?.content || ''}
                    onChange={(e) => handleUpdateSection(currentSection, 'content', e.target.value)}
                    placeholder="Enter section content (use \n for line breaks)"
                    rows="8"
                  />
                </div>

                <div className="form-group">
                  <label>Video URL (optional)</label>
                  <input
                    type="url"
                    value={formData.content.sections[currentSection]?.video?.src || ''}
                    onChange={(e) => handleUpdateSection(currentSection, 'video', {
                      ...formData.content.sections[currentSection]?.video,
                      src: e.target.value
                    })}
                    placeholder="https://www.youtube.com/embed/..."
                  />
                </div>
              </div>
            )}
          </div>

          <div className="form-section">
            <h2 className="text-xl font-bold mb-4">Module Assignments</h2>
            <p className="text-gray-600 mb-4">Assign this module to specific roles, departments, or individual users.</p>
            
            <div className="assignment-grid">
              <div className="assignment-group">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <Users size={20} style={{ marginRight: '8px' }} />
                  Assign by Role
                </h3>
                <div className="checkbox-group">
                  {getAllRoles().map(role => (
                    <label key={role} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={formData.assignments.roles?.includes(role) || false}
                        onChange={(e) => handleRoleAssignment(role, e.target.checked)}
                      />
                      <span className="ml-2 capitalize">{role}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="assignment-group">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <Building size={20} style={{ marginRight: '8px' }} />
                  Assign by Department
                </h3>
                <div className="checkbox-group">
                  {getAllDepartments().map(department => (
                    <label key={department} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={formData.assignments.departments?.includes(department) || false}
                        onChange={(e) => handleDepartmentAssignment(department, e.target.checked)}
                      />
                      <span className="ml-2">{department}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="assignment-group">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <UserCheck size={20} style={{ marginRight: '8px' }} />
                  Assign by Designation
                </h3>
                <div className="checkbox-group">
                  {getAllDesignations().map(designation => (
                    <label key={designation} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={formData.assignments.designations?.includes(designation) || false}
                        onChange={(e) => handleDesignationAssignment(designation, e.target.checked)}
                      />
                      <span className="ml-2">{designation}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="assignment-group">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <Award size={20} style={{ marginRight: '8px' }} />
                  Assign by Specialization
                </h3>
                <div className="checkbox-group">
                  {getAllSpecializations().map(specialization => (
                    <label key={specialization} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={formData.assignments.specializations?.includes(specialization) || false}
                        onChange={(e) => handleSpecializationAssignment(specialization, e.target.checked)}
                      />
                      <span className="ml-2">{specialization}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="assignment-group">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <UserCheck size={20} style={{ marginRight: '8px' }} />
                  Assign to Individual Users
                </h3>
                <div className="user-search">
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full mb-3"
                  />
                  <div className="user-list">
                    {filteredUsers.map(user => (
                      <label key={user.id} className="checkbox-item user-item">
                        <input
                          type="checkbox"
                          checked={formData.assignments.users?.includes(user.email) || false}
                          onChange={(e) => handleUserAssignment(user.email, e.target.checked)}
                        />
                        <div className="ml-2">
                          <div className="font-medium">{user.name}</div>
                          <div className="text-sm text-gray-600">{user.email} • {user.department}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <h2 className="text-xl font-bold">Quiz Questions</h2>
              <button onClick={handleAddQuiz} className="btn btn-primary btn-sm">
                <Plus size={16} style={{ marginRight: '4px' }} />
                Add Question
              </button>
            </div>

            {formData.quiz.length > 0 && (
              <div className="quiz-tabs">
                {formData.quiz.map((question, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentQuiz(index)}
                    className={`tab ${currentQuiz === index ? 'active' : ''}`}
                  >
                    Q{index + 1}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveQuiz(index);
                      }}
                      className="remove-btn"
                    >
                      <X size={14} />
                    </button>
                  </button>
                ))}
              </div>
            )}

            {formData.quiz.length > 0 && (
              <div className="quiz-editor">
                <div className="form-group">
                  <label>Question</label>
                  <input
                    type="text"
                    value={formData.quiz[currentQuiz]?.question || ''}
                    onChange={(e) => handleUpdateQuiz(currentQuiz, 'question', e.target.value)}
                    placeholder="Enter question"
                  />
                </div>

                <div className="form-group">
                  <label>Answer Options</label>
                  {formData.quiz[currentQuiz]?.options?.map((option, optionIndex) => (
                    <div key={optionIndex} className="option-input">
                      <input
                        type="radio"
                        name={`correct-${currentQuiz}`}
                        checked={formData.quiz[currentQuiz]?.correct === optionIndex}
                        onChange={() => handleUpdateQuiz(currentQuiz, 'correct', optionIndex)}
                      />
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => {
                          const newOptions = [...formData.quiz[currentQuiz].options];
                          newOptions[optionIndex] = e.target.value;
                          handleUpdateQuiz(currentQuiz, 'options', newOptions);
                        }}
                        placeholder={`Option ${optionIndex + 1}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button onClick={() => setShowCreateForm(false)} className="btn btn-secondary">
              Cancel
            </button>
            <button onClick={handleSave} className="btn btn-primary">
              <Save size={16} style={{ marginRight: '8px' }} />
              {isEditing ? 'Update Module' : 'Create Module'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="module-management-header">
        <button onClick={onBack} className="btn btn-secondary back-to-dashboard">
          <ArrowLeft size={16} style={{ marginRight: '8px' }} />
          Back to Dashboard
        </button>
        <div>
          <h1 className="text-3xl font-bold">Module Management</h1>
          <p className="text-lg text-gray-600">Create, edit, and manage training modules</p>
        </div>
        <button onClick={handleCreate} className="btn btn-primary">
          <Plus size={16} style={{ marginRight: '8px' }} />
          Create New Module
        </button>
      </div>

      <div className="modules-grid">
        {modules.map((module) => (
          <div key={module.id} className="module-card">
            <div className="module-card-header">
              <div className="module-icon">
                {module.icon}
              </div>
              <div className="module-actions">
                <button
                  onClick={() => handleEdit(module)}
                  className="btn-icon"
                  title="Edit Module"
                >
                  <Edit size={16} />
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(module.id)}
                  className="btn-icon btn-danger"
                  title="Delete Module"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            <div className="module-content">
              <h3 className="module-title">{module.title}</h3>
              <p className="module-description">{module.description}</p>
              
              {module.tags && module.tags.length > 0 && (
                <div className="module-tags">
                  {module.tags.map(tag => (
                    <span key={tag} className="module-tag">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              
              <div className="module-meta">
                <div className="meta-item">
                  <Clock size={14} />
                  <span>{module.duration} min</span>
                </div>
                <div className="meta-item">
                  <Award size={14} />
                  <span>{module.difficulty}</span>
                </div>
                <div className="meta-item">
                  <BookOpen size={14} />
                  <span>{module.content.sections.length} sections</span>
                </div>
              </div>

              <div className="module-stats">
                <div className="stat">
                  <span className="stat-label">Sections:</span>
                  <span className="stat-value">{module.content.sections.length}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Quiz Questions:</span>
                  <span className="stat-value">{module.quiz.length}</span>
                </div>
              </div>

              <div className="module-assignments">
                <div 
                  className="assignment-summary clickable"
                  onClick={() => handleViewAssignedUsers(module)}
                  title="Click to view assigned users"
                >
                  <span className="assignment-label">Assignments:</span>
                  <span className="assignment-value">{getAssignmentSummary(module)}</span>
                  <Users size={14} style={{ marginLeft: '8px', opacity: 0.7 }} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Confirm Delete</h3>
              <button onClick={() => setShowDeleteConfirm(null)} className="btn-icon">
                <X size={16} />
              </button>
            </div>
            <div className="modal-content">
              <p>Are you sure you want to delete this module? This action cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowDeleteConfirm(null)} className="btn btn-secondary">
                Cancel
              </button>
              <button onClick={() => handleDelete(showDeleteConfirm)} className="btn btn-danger">
                <Trash2 size={16} style={{ marginRight: '8px' }} />
                Delete Module
              </button>
            </div>
          </div>
        </div>
      )}

      <AssignedUsersModal
        isOpen={showAssignedUsers}
        onClose={() => setShowAssignedUsers(false)}
        assignments={selectedModule?.assignments}
        title={`Assigned Users - ${selectedModule?.title}`}
      />
    </div>
  );
};

export default ModuleManagement;
